import { useEffect, useRef } from "react";
import {
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
  useParams,
  useRoutes,
  type RouteObject,
} from "react-router-dom";
import Layout from "@/components/Layout";
import { useDeploymentStore } from "@/store/deployment.store";
import { useAuthStore } from "@/store/auth.store";
import FeishuCallback from "@/pages/FeishuCallback";
import { agentRoutes } from "@/router/agent";
import { applicationRoutes } from "@/router/application";
import AccountLogin from "@/pages/auth/AccountLogin";
import OidcCallback from "@/pages/auth/OidcCallback";
import SAMLCallback from "@/pages/auth/SAMLCallback";
import SSOTestComplete from "@/pages/auth/SSOTestComplete";
import SAMLSSO from "@/pages/auth/SAMLSSO";
import SsoLogin from "@/pages/auth/SsoLogin";
import { getLoginPath } from "@/pages/auth/loginMode";
import {  redirectToOidcLogin } from "@/pages/auth/oidc";
import type { LoginMode } from "@/config/deployment/deployment.types";
import { channelRoutes } from "@/router/channel";
import { commonRoutes } from "@/router/common";
import { credentialsRoutes } from "@/router/credentials";
import { iamRoutes } from "@/router/iam";
import { mcpRoutes } from "@/router/mcp";
import { memoryRoutes } from "@/router/memory";
import { ragLegacyRedirectRoutes, ragRoutes } from "@/router/rag";
import { sandboxRoutes } from "@/router/sandbox";
import { skillRoutes } from "@/router/skill";


function AuthGuard({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const loginMode = useDeploymentStore((state) => state.profile.login.mode);

  if (!token) {
    return <ConfiguredLoginRedirect mode={loginMode} />;
  }

  return <>{children}</>;
}

function AuthenticatedLayout() {
  return (
    <AuthGuard>
      <Layout>
        <Outlet />
      </Layout>
    </AuthGuard>
  );
}

function ConfiguredLoginRedirect({ mode }: { mode: LoginMode }) {
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    logout();
    if (mode === "oidc") {
      redirectToOidcLogin();
      return;
    }
    navigate(getLoginPath(mode), { replace: true });
  }, [logout, mode, navigate]);

  return null;
}

function LoginEntry({ mode }: { mode: LoginMode }) {
  if (mode === "oidc") {
    return <ConfiguredLoginRedirect mode={mode} />;
  }

  return mode === "saml" ? <SsoLogin /> : <AccountLogin />;
}

function LegacySkillClassDetailRedirect() {
  const { id = "" } = useParams<{ id: string }>();
  return <Navigate to={`/plugins/skills/group-classes/${id}`} replace />;
}

export function AppRoutes() {
  const { search } = useLocation();
  const hasSamlToken = Boolean(new URLSearchParams(search).get("token"));
  const token = useAuthStore((state) => state.token);
  const loginMode = useDeploymentStore((state) => state.profile.login.mode);
  const hasValidToken = Boolean(token);

  const routes: RouteObject[] = [
    { path: "/login/sso", element: <SsoLogin /> },
    { path: "/saml/test-complete", element: <SSOTestComplete /> },
    {
      path: "/login/*",
      element: hasValidToken ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <LoginEntry mode={loginMode} />
      ),
    },
    // Preserve the legacy SAML entry and callback URLs used by IAM / IdP configuration.
    { path: "/saml/:domain/SSO", element: <SAMLSSO /> },
    { path: "/saml/callback", element: <SAMLCallback /> },
    { path: "/saml/callback/success", element: <SAMLCallback /> },
    {
      path: "/saml/*",
      element: hasValidToken ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <ConfiguredLoginRedirect mode={loginMode} />
      ),
    },
    {
      path: "/home",
      element: loginMode === "saml" || hasSamlToken ? (
        <SAMLCallback />
      ) : hasValidToken ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <ConfiguredLoginRedirect mode={loginMode} />
      ),
    },
    { path: "/feishu/callback", element: <FeishuCallback /> },
    {
      path: "/",
      element:
        loginMode === "oidc" ? (
          <OidcCallback />
        ) : hasValidToken ? (
          <Navigate to="/dashboard" replace />
        ) : (
          <LoginEntry mode={loginMode} />
        ),
    },
    ...ragLegacyRedirectRoutes,
    { path: "/skills", element: <Navigate to="/plugins/skills" replace /> },
    {
      path: "/skills/classes/:id",
      element: <LegacySkillClassDetailRedirect />,
    },
    {
      element: <AuthenticatedLayout />,
      children: [
        ...commonRoutes,
        ...agentRoutes,
        ...applicationRoutes,
        ...channelRoutes,
        ...credentialsRoutes,
        ...iamRoutes,
        ...mcpRoutes,
        ...memoryRoutes,
        ...ragRoutes,
        ...sandboxRoutes,
        ...skillRoutes,
      ],
    },
    { path: "*", element: <Navigate to="/dashboard" replace /> },
  ];

  return useRoutes(routes);
}
